/**
 * Offline License Banner
 *
 * Displays a warning when:
 * - The session is stale and needs re-authentication (highest priority)
 * - A system clock rollback was detected
 * - The offline license cache is expiring
 *
 * Prompts users to go online to verify their subscription, or to sign in again
 * when the session can no longer be refreshed.
 */

import { useState } from 'react';
import { AlertTriangle, Wifi, Clock, LogIn } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { RandomDotmSpinner } from '../../../components/ui/RandomDotmSpinner';

interface OfflineLicenseBannerProps {
  /** Days remaining until cache expires */
  daysRemaining: number;
  /** Optional callback when user clicks to verify */
  onVerify?: () => void | Promise<void>;
  /** Whether the banner is shown due to clock rollback detection */
  clockRollbackDetected?: boolean;
  /** Hours of clock rollback (if detected) */
  rollbackHours?: number;
  /** The session is stale (cannot be refreshed) — show a re-logon prompt. */
  sessionStale?: boolean;
  /** Callback when the user clicks to re-sign-in (opens the auth dialog). */
  onReSignIn?: () => void;
}

/**
 * Warning banner shown when the session is stale, the clock rolled back, or the
 * offline license cache is about to expire. Prompts re-verification / re-logon.
 */
export function OfflineLicenseBanner({
  daysRemaining,
  onVerify,
  clockRollbackDetected,
  rollbackHours,
  sessionStale,
  onReSignIn,
}: OfflineLicenseBannerProps) {
  // Clock rollback is always urgent
  const isUrgent = daysRemaining <= 2 || clockRollbackDetected;

  // Track an in-flight verify so the button can't be double-fired and the user
  // gets feedback during the (possibly multi-second) online round-trip.
  const [busy, setBusy] = useState(false);
  const handleVerify = async () => {
    if (!onVerify || busy) return;
    setBusy(true);
    try {
      await onVerify();
    } finally {
      setBusy(false);
    }
  };

  const verifyButton = onVerify && (
    <Button
      variant="outline"
      size="sm"
      onClick={handleVerify}
      disabled={busy}
      className="shrink-0"
      data-testid="verify-subscription-button"
    >
      {busy ? <RandomDotmSpinner size={12} className="mr-2 h-3 w-3" /> : <Wifi className="mr-2 h-3 w-3" />}
      Verify Now
    </Button>
  );

  // Stale session — highest priority: Pro may be intact offline, but the OIDC
  // session can no longer be refreshed, so the user must re-authenticate. Shown
  // regardless of Personal/Pro mode (the footer prompt only covers Personal).
  if (sessionStale) {
    return (
      <div
        className="flex items-center justify-between gap-3 px-4 py-2 bg-destructive/10 border-destructive/20 border-t"
        role="alert"
        data-testid="offline-license-banner"
      >
        <div className="flex items-center gap-2 text-sm">
          <AlertTriangle className="h-4 w-4 text-destructive" />
          <span className="text-destructive">
            Your Pro session needs re-authentication — sign in to keep Pro features.
          </span>
        </div>

        {onReSignIn && (
          <Button
            variant="outline"
            size="sm"
            onClick={onReSignIn}
            className="shrink-0"
            data-testid="resignin-button"
          >
            <LogIn className="mr-2 h-3 w-3" />
            Sign In
          </Button>
        )}
      </div>
    );
  }

  // Clock rollback message
  if (clockRollbackDetected) {
    return (
      <div
        className="flex items-center justify-between gap-3 px-4 py-2 bg-destructive/10 border-destructive/20 border-t"
        role="alert"
        data-testid="offline-license-banner"
      >
        <div className="flex items-center gap-2 text-sm">
          <Clock className="h-4 w-4 text-destructive" />
          <span className="text-destructive">
            {rollbackHours
              ? `System clock appears to have been rolled back by ${rollbackHours} hours. Please go online to verify your subscription.`
              : 'System clock inconsistency detected. Please go online to verify your subscription.'}
          </span>
        </div>

        {verifyButton}
      </div>
    );
  }

  // Cache expiring message
  return (
    <div
      className={`flex items-center justify-between gap-3 px-4 py-2 ${
        isUrgent
          ? 'bg-destructive/10 border-destructive/20'
          : 'bg-amber-500/10 border-amber-500/20'
      } border-t`}
      role="alert"
      data-testid="offline-license-banner"
    >
      <div className="flex items-center gap-2 text-sm">
        <AlertTriangle
          className={`h-4 w-4 ${isUrgent ? 'text-destructive' : 'text-warning'}`}
        />
        <span className={isUrgent ? 'text-destructive' : 'text-warning'}>
          {daysRemaining === 0 ? (
            'Your Pro subscription verification expired. Please go online to continue using Pro features.'
          ) : daysRemaining === 1 ? (
            'Your Pro subscription verification expires tomorrow. Please go online to verify.'
          ) : (
            `Pro subscription verification expires in ${daysRemaining} days. Please go online to verify.`
          )}
        </span>
      </div>

      {verifyButton}
    </div>
  );
}
